import {
    type FC,
    useRef
} from "react";
import {
    Pressable,
    Platform,
    View
} from "react-native";
import type IFileUploadDropzoneProps from "./type";
import stylesheet, {
    DROPZONE_ACTIVE_TITLE_VARIANTS,
    DROPZONE_DESCRIPTION_VARIANTS,
    getDropzoneColors,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import {
    useFileDrop
} from "../../hook";
import {
    Upload as UploadIcon
} from "lucide-react-native";
import {
    getControlMeasures
} from "../../../../helpers/theme/control";
import {
    getFocusRingStyle
} from "../../../../helpers/theme/state";
import {
    formatNumber
} from "../../../../helpers/locale";
import AnimatedIcon from "../../../animatedIcon";
import StateLayer from "../../../stateLayer";
import Text from "../../../text";
import Kbd from "../../../kbd";

const FileUploadDropzone: FC<IFileUploadDropzoneProps> = ({
    isDisabled = false,
    isInvalid = false,
    customLocalize,
    descriptionId,
    customTheme,
    description,
    isMultiple,
    onHoverOut,
    onPressOut,
    onHoverIn,
    onPressIn,
    isTouch,
    onFiles,
    onFocus,
    onPress,
    titleId,
    onBlur,
    style,
    size,
    ...props
}) => {
    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const nodeRef = useRef<View>(null);

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        isDisabled,
        onHoverOut,
        onPressOut,
        onHoverIn,
        onPressIn,
        onFocus,
        onBlur
    });

    const {
        isDragOver,
        dragCount
    } = useFileDrop({
        isEnabled: !isDisabled && !isTouch,
        onFiles,
        nodeRef
    });

    const currentMeasures = getControlMeasures({
        controlPaddings,
        controlHeights,
        shapeConfigs,
        iconSizes,
        size
    });

    const currentColors = getDropzoneColors({
        isInteracted: isHovered || isPressed,
        isInvalid: isInvalid && !isDisabled,
        isDisabled,
        isDragOver,
        colors
    });

    const {
        surface: surfaceDynamicStyle,
        icon: iconDynamicStyle
    } = useStyles({
        isInvalid: isInvalid && !isDisabled,
        borderRadius: radiuses.surface,
        controlPaddings,
        controlHeights,
        currentColors,
        shapeConfigs,
        isDragOver,
        borders,
        colors,
        spaces,
        size
    });

    const descriptionVariant = DROPZONE_DESCRIPTION_VARIANTS[size];

    const isShowPasteHint = Platform.OS === "web" && !isTouch && !isDisabled && !isDragOver;

    const renderTitle = () => {
        if(isDragOver) {
            return <Text
                variant={DROPZONE_ACTIVE_TITLE_VARIANTS[size]}
                customColor={currentColors.titleColor}
                customTheme={customTheme}
                style={stylesheet.title}
                id={titleId}
            >
                {localize("file-upload-drop-here")}
            </Text>;
        }

        const linkText = isTouch ? localize("file-upload-tap-to-add") : localize(isMultiple ? "file-upload-choose-files" : "file-upload-choose-file");

        return <Text
            customColor={currentColors.titleColor}
            variant={currentMeasures.labelVariant}
            customTheme={customTheme}
            style={stylesheet.title}
            id={titleId}
        >
            <Text
                style={isDisabled ? undefined : stylesheet.link}
                variant={currentMeasures.labelVariant}
                customColor={currentColors.linkColor}
                customTheme={customTheme}
            >
                {linkText}
            </Text>
            {isTouch ? "" : ` ${localize("file-upload-or-drag")}`}
        </Text>;
    };

    const renderPasteHint = (isAfterText: boolean) => {
        if(!isShowPasteHint) {
            return null;
        }

        return <>
            {isAfterText ? ` · ${localize("file-upload-or-paste")} ` : `${localize("file-upload-or-paste")} `}
            <Kbd
                customLocalize={customLocalize}
                style={stylesheet.inline}
                customTheme={customTheme}
                keys="mod+V"
                size="small"
            />
        </>;
    };

    const renderDescription = () => {
        const text = isDragOver && dragCount ? localize("file-upload-drop-count", [
            formatNumber({
                locale: activeLocale,
                value: dragCount
            })
        ]) : description;

        if(!text && !isShowPasteHint) {
            return <View
                style={stylesheet.description}
                id={descriptionId}
            />;
        }

        return <View
            style={stylesheet.description}
            id={descriptionId}
        >
            <Text
                customColor={currentColors.descriptionColor}
                variant={descriptionVariant}
                style={stylesheet.tabular}
                customTheme={customTheme}
            >
                {text}
                {renderPasteHint(!!text)}
            </Text>
        </View>;
    };

    return <Pressable
        {...props}
        {...interactionHandlers}
        style={[
            style,
            stylesheet.container,
            isDisabled ? stylesheet.disabledCursor : undefined
        ]}
        aria-disabled={isDisabled}
        disabled={isDisabled}
        onPress={onPress}
        role="button"
        ref={nodeRef}
    >
        <View
            style={[
                stylesheet.surface,
                surfaceDynamicStyle,
                getFocusRingStyle({
                    colorKey: isInvalid ? "error" : "default",
                    isFocusVisible,
                    borders,
                    colors
                })
            ]}
        >
            <StateLayer
                isHovered={!isDisabled && !isDragOver && isHovered}
                isPressed={!isDisabled && !isDragOver && isPressed}
                borderRadius={radiuses.surface}
                borderWidth={borders.line}
                customTheme={customTheme}
                overlayColor="default"
            />
            <View
                style={[
                    stylesheet.icon,
                    iconDynamicStyle
                ]}
                aria-hidden={true}
            >
                <AnimatedIcon
                    animation={isDragOver ? "signature" : "none"}
                    customColor={currentColors.iconColor}
                    size={currentMeasures.iconSize}
                    customTheme={customTheme}
                    animationTrigger="mount"
                    icon={UploadIcon}
                />
            </View>
            {renderTitle()}
            {renderDescription()}
        </View>
    </Pressable>;
};
export default FileUploadDropzone;
